import { Grid, Paper, Tooltip, Typography } from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Skeleton } from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import theme from "Theme/AppTheme";

interface AcxStatusCardProps {
    primaryContent: React.ReactNode | string | number;
    caption?: string;
    captionVariant?:
        | "h1"
        | "h2"
        | "h3"
        | "h4"
        | "h5"
        | "h6"
        | "subtitle1"
        | "subtitle2"
        | "body1"
        | "body2"
        | "caption"
        | "button";
    fluctuationIsPositive?: boolean;
    fluctuationUnit?: string;
    fluctuationAmount?: number;
    containerStyles?: React.CSSProperties;
    isLoading?: boolean;
    tooltipText?: string;
    wrapTitle?: boolean;
}

type Props = AcxStatusCardProps;

const AcxStatusCard: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = observer((props) => {
    const {
        caption,
        captionVariant,
        primaryContent,
        fluctuationIsPositive,
        fluctuationUnit,
        fluctuationAmount,
        containerStyles,
        isLoading,
        tooltipText,
        wrapTitle,
    } = props;
    const [displayFluctuation, setFluctuationDisplay] =
        React.useState<boolean>(false);

    React.useEffect(() => {
        if (
            fluctuationIsPositive !== undefined &&
            fluctuationAmount &&
            fluctuationUnit
        ) {
            setFluctuationDisplay(true);
        } else {
            setFluctuationDisplay(false);
        }
    }, [fluctuationAmount, fluctuationIsPositive, fluctuationUnit]);

    return (
        <Paper
            sx={{ display: "flex", justifyContent: "center" }}
            style={containerStyles ?? {}}
        >
            <Grid
                container
                item
                xs={12}
                sx={{ padding: "0.5rem", alignContent: "space-between" }}
            >
                {isLoading ? (
                    <Grid item style={{ width: "100%", height: "100%" }}>
                        <Skeleton variant="text" width="100%" height="30%" />
                        <Skeleton variant="text" width="100%" height="70%" />
                    </Grid>
                ) : (
                    <>
                        <Grid
                            item
                            xs={12}
                            sx={
                                wrapTitle
                                    ? {
                                          display: "block",
                                          textOverflow: "ellipsis",
                                          wordWrap: "break-word",
                                          overflow: "clip",
                                          maxHeight: "3.2em",
                                      }
                                    : {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "calc(100% - 1rem)",
                                      }
                            }
                        >
                            {caption && (
                                <Tooltip title={tooltipText ?? caption}>
                                    <Typography
                                        variant={captionVariant ?? "caption"}
                                    >
                                        {caption}
                                    </Typography>
                                </Tooltip>
                            )}
                        </Grid>

                        <Grid
                            container
                            item
                            xs={12}
                            sx={{ alignItems: "center", margin: "0.25rem 0" }}
                        >
                            <Grid item xs={12}>
                                {primaryContent}
                            </Grid>

                            {displayFluctuation && (
                                <Grid
                                    container
                                    item
                                    xs={5}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        borderRadius: "0.25rem",
                                        backgroundColor: fluctuationIsPositive
                                            ? theme.palette.green.light
                                            : theme.palette.red.light,
                                    }}
                                >
                                    {fluctuationIsPositive ? (
                                        <TrendingUpIcon />
                                    ) : (
                                        <TrendingDownIcon />
                                    )}

                                    <Typography
                                        variant="body2"
                                        sx={{ marginLeft: "0.25rem" }}
                                    >
                                        {fluctuationAmount}
                                        {fluctuationUnit}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </>
                )}
            </Grid>
        </Paper>
    );
});

export default AcxStatusCard;
